/**
 * Arquivo: src/utils/xlsxLite.ts
 * Versão: 4.10.68
 * Data: 11/09/2026
 *
 * Leitor XLSX mínimo, sem dependências externas.
 * Lê apenas o necessário para importação de faturas: planilhas, strings e valores.
 */

export interface XlsxSheet {
  name: string
  rows: Array<Array<string | number | boolean | null>>
}

interface ZipEntry {
  name: string
  method: number
  compressedSize: number
  localOffset: number
}

function u16(view: DataView, offset: number) {
  return view.getUint16(offset, true)
}

function u32(view: DataView, offset: number) {
  return view.getUint32(offset, true)
}

function findEocd(view: DataView) {
  const min = Math.max(0, view.byteLength - 0xffff - 22)
  for (let i = view.byteLength - 22; i >= min; i -= 1) {
    if (u32(view, i) === 0x06054b50) return i
  }
  return -1
}

function listEntries(buffer: ArrayBuffer): ZipEntry[] {
  const view = new DataView(buffer)
  const eocd = findEocd(view)
  if (eocd < 0) throw new Error('Arquivo XLSX inválido: diretório ZIP não encontrado.')

  const totalEntries = u16(view, eocd + 10)
  const centralOffset = u32(view, eocd + 16)
  const decoder = new TextDecoder('utf-8')
  const out: ZipEntry[] = []
  let p = centralOffset

  for (let i = 0; i < totalEntries; i += 1) {
    if (u32(view, p) !== 0x02014b50) throw new Error('Arquivo XLSX inválido: entrada ZIP corrompida.')
    const method = u16(view, p + 10)
    const compressedSize = u32(view, p + 20)
    const nameLength = u16(view, p + 28)
    const extraLength = u16(view, p + 30)
    const commentLength = u16(view, p + 32)
    const localOffset = u32(view, p + 42)
    const nameBytes = new Uint8Array(buffer, p + 46, nameLength)
    const name = decoder.decode(nameBytes)
    out.push({ name, method, compressedSize, localOffset })
    p += 46 + nameLength + extraLength + commentLength
  }

  return out
}

async function inflateRaw(bytes: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('Seu navegador não oferece suporte à leitura de XLSX. Tente importar pelo Chrome ou Edge atualizado no computador.')
  }

  // Cria um ArrayBuffer próprio antes de montar o Blob.
  // Isso evita a incompatibilidade do TypeScript entre Uint8Array<ArrayBufferLike>
  // e BlobPart quando o buffer de origem poderia ser SharedArrayBuffer.
  const payload = new ArrayBuffer(bytes.byteLength)
  new Uint8Array(payload).set(bytes)

  const stream = new Blob([payload])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw' as CompressionFormat))

  return new Uint8Array(await new Response(stream).arrayBuffer())
}

async function readEntry(buffer: ArrayBuffer, entry: ZipEntry): Promise<Uint8Array> {
  const view = new DataView(buffer)
  const p = entry.localOffset
  if (u32(view, p) !== 0x04034b50) throw new Error(`Arquivo XLSX inválido: entrada ${entry.name} não localizada.`)
  const nameLength = u16(view, p + 26)
  const extraLength = u16(view, p + 28)
  const start = p + 30 + nameLength + extraLength
  const bytes = new Uint8Array(buffer, start, entry.compressedSize)
  if (entry.method === 0) return new Uint8Array(bytes)
  if (entry.method === 8) return inflateRaw(bytes)
  throw new Error(`Compressão XLSX não suportada (${entry.method}).`)
}

function parseXml(text: string) {
  const doc = new DOMParser().parseFromString(text, 'application/xml')
  if (doc.getElementsByTagName('parsererror').length) throw new Error('Arquivo XLSX inválido: XML corrompido.')
  return doc
}

function textContent(node: Element | null) {
  return node?.textContent || ''
}

function colIndex(ref: string) {
  const letters = ref.match(/^[A-Z]+/i)?.[0]?.toUpperCase() || 'A'
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}

function normalizePath(base: string, target: string) {
  if (target.startsWith('/')) return target.replace(/^\//, '')
  const parts = `${base}/${target}`.split('/')
  const out: string[] = []
  for (const part of parts) {
    if (!part || part === '.') continue
    if (part === '..') out.pop()
    else out.push(part)
  }
  return out.join('/')
}

export async function readXlsx(file: File): Promise<XlsxSheet[]> {
  const buffer = await file.arrayBuffer()
  const entries = listEntries(buffer)
  const map = new Map(entries.map(e => [e.name, e]))
  const decoder = new TextDecoder('utf-8')

  const readText = async (name: string) => {
    const entry = map.get(name)
    if (!entry) return ''
    return decoder.decode(await readEntry(buffer, entry))
  }

  const workbookText = await readText('xl/workbook.xml')
  const relsText = await readText('xl/_rels/workbook.xml.rels')
  if (!workbookText || !relsText) throw new Error('Arquivo XLSX inválido: estrutura da pasta de trabalho não encontrada.')

  const sharedText = await readText('xl/sharedStrings.xml')
  const sharedStrings: string[] = []
  if (sharedText) {
    const doc = parseXml(sharedText)
    for (const si of Array.from(doc.getElementsByTagName('si'))) {
      sharedStrings.push(Array.from(si.getElementsByTagName('t')).map(t => t.textContent || '').join(''))
    }
  }

  const relDoc = parseXml(relsText)
  const relMap = new Map<string, string>()
  for (const rel of Array.from(relDoc.getElementsByTagName('Relationship'))) {
    const id = rel.getAttribute('Id') || ''
    const target = rel.getAttribute('Target') || ''
    if (id && target) relMap.set(id, normalizePath('xl', target))
  }

  const workbookDoc = parseXml(workbookText)
  const sheets = Array.from(workbookDoc.getElementsByTagName('sheet'))
  const result: XlsxSheet[] = []

  for (const sheet of sheets) {
    const name = sheet.getAttribute('name') || 'Planilha'
    const relId = sheet.getAttribute('r:id') || sheet.getAttributeNS('http://schemas.openxmlformats.org/officeDocument/2006/relationships', 'id') || ''
    const path = relMap.get(relId)
    if (!path) continue
    const xml = await readText(path)
    if (!xml) continue
    const doc = parseXml(xml)
    const rows: XlsxSheet['rows'] = []

    for (const rowNode of Array.from(doc.getElementsByTagName('row'))) {
      const rowNumber = Number(rowNode.getAttribute('r') || rows.length + 1)
      const row: Array<string | number | boolean | null> = []
      for (const c of Array.from(rowNode.getElementsByTagName('c'))) {
        const ref = c.getAttribute('r') || 'A1'
        const idx = colIndex(ref)
        const type = c.getAttribute('t') || ''
        let value: string | number | boolean | null = null

        if (type === 'inlineStr') {
          value = Array.from(c.getElementsByTagName('t')).map(t => t.textContent || '').join('')
        } else {
          const raw = textContent(c.getElementsByTagName('v')[0] || null)
          if (type === 's') value = sharedStrings[Number(raw)] ?? raw
          else if (type === 'b') value = raw === '1'
          else if (type === 'str' || type === 'e') value = raw
          else if (raw !== '') {
            const n = Number(raw)
            value = Number.isFinite(n) ? n : raw
          }
        }
        row[idx] = value
      }
      while (rows.length < rowNumber - 1) rows.push([])
      rows[rowNumber - 1] = row
    }

    result.push({ name, rows })
  }

  return result
}
